import {Component, DestroyRef, inject, OnDestroy, OnInit, Renderer2, ViewEncapsulation} from '@angular/core';

import {AccountingInfoStepComponent} from './steps/step-2-accounting-info/accounting-info-step.component';
import {ServiceInfoStepComponent} from './steps/step-3-service-info/service-info-step.component';
import {SummaryStepComponent} from './steps/step-4-summary/summary-step.component';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {FaIconComponent, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {RmaValidators} from '../shared/rma-validators';
import {CompanyInfoStepComponent} from './steps/step-1-company-info/company-info-step.component';
import {Step, StepList, StepPanel, StepPanels, Stepper} from "primeng/stepper";
import {ClientCreationService} from './steps/services/client-creation.service';
import * as uuid from "uuid";
import {Router} from '@angular/router';
import {RoutesService} from '../service/routes.service';
import {VatMethod2} from '../enums/vat-method';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ToasterService} from '../service/toaster.service';
import {TranslateService} from '@ngx-translate/core';
import {RmaLoadingComponent} from '../rma-components/rma-loading/rma-loading.component';
import {noop, Observable, Subject, switchMap} from 'rxjs';
import {AsyncPipe, CommonModule} from '@angular/common';
import {RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module, ReCaptchaV3Service} from 'ng-recaptcha';
import {environment} from '@environments/environment';
import {StateService} from "../service/state/state.service";
import {UserService} from "../service/user.service";
import {SimpleLayoutComponent} from '../layouts/simple-layout.component';


@Component({
  selector: 'ibp-client-creation',
  templateUrl: './client-creation.component.html',
  styleUrls: ['./client-creation.component.scss'],
  standalone: true,
  imports: [
    Stepper,
    StepList,
    Step,
    StepPanels,
    StepPanel,
    AccountingInfoStepComponent,
    ServiceInfoStepComponent,
    SummaryStepComponent,
    ReactiveFormsModule,
    FaIconComponent,
    CompanyInfoStepComponent,
    RmaLoadingComponent,
    AsyncPipe,
    RecaptchaV3Module,
    SimpleLayoutComponent,
    CommonModule,
    FontAwesomeModule,
    StepList,
    Step,
    StepPanel,
    StepPanels,
  ],
  providers: [
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.captchaSiteKey }],
  encapsulation: ViewEncapsulation.None,
})
export class ClientCreationComponent implements OnInit, OnDestroy {
  private fb: FormBuilder = inject(FormBuilder);
  private clientCreationService = inject(ClientCreationService);
  private router: Router = inject(Router);
  private routes: RoutesService = inject(RoutesService);
  private toaster: ToasterService = inject(ToasterService);
  private translate: TranslateService = inject(TranslateService);
  private recaptchaV3Service: ReCaptchaV3Service = inject(ReCaptchaV3Service);
  private renderer: Renderer2 = inject(Renderer2);
  private stateService = inject(StateService);
  private userService = inject(UserService);
  readonly #destroyRef = inject(DestroyRef);

  public clientCreationForm: FormGroup;
  public isLoading$: Observable<boolean>;
  public activeStep = 0;

  private clientListReloadTrigger$ = new Subject<boolean>();

  public ngOnInit(): void {
    this.renderer.addClass(document.body, 'recaptcha');
    this.prepareClientCreationForm();
    this.isLoading$ = this.clientCreationService.getClientCreationLoading();
    this.setupClientsListUpdateSubscription();
  }

  public ngOnDestroy(): void {
    this.renderer.removeClass(document.body, 'recaptcha');
  }

  public cancelClientCreation() {
    this.router.navigate(this.routes.authClientSelection());
  }

  public submitClient() {
    const requestId: string = uuid.v4();
    this.clientCreationForm.get('requestId').setValue(requestId);
    this.clientCreationService.setClientCreationLoading(true);

    this.recaptchaV3Service.execute('newClient').pipe(
      switchMap(token => {
        this.clientCreationForm.get('captchaToken').setValue(token);
        return this.clientCreationService.submitClient(this.clientCreationForm.value)
      }),
      takeUntilDestroyed(this.#destroyRef),
    ).subscribe(clientCreationResponse => {
      if (clientCreationResponse.clientNameId) {
        this.clientListReloadTrigger$.next(true);
        this.toaster.pop('success', this.translate.instant('CLIENT_CREATION.NOTIFICATIONS.CLIENT_CREATED'), '');
        this.router.navigate(this.routes.dashboard(clientCreationResponse.clientNameId));
      }
    }, error => {
      this.clientCreationService.setClientCreationLoading(false);
      this.toaster.pop('error', this.translate.instant('CLIENT_CREATION.NOTIFICATIONS.ERROR'), '');
    }, () => {
      this.clientCreationService.setClientCreationLoading(false);
    });
  }

  private setupClientsListUpdateSubscription() {
    this.clientListReloadTrigger$.pipe(
      switchMap(() => this.stateService.user$),
      switchMap(user => this.userService.getLoadUserClientRoles(user.id)),
      takeUntilDestroyed(this.#destroyRef),
    ).subscribe(noop);
  }

  private orderConfirmedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value !== true) {
        return {orderNotConfirmed: true};
      }

      return null;
    };
  }

  private prepareClientCreationForm() {
    this.clientCreationForm = this.fb.group({
      requestId: [],
      captchaToken: ['', []],
      companyInfo: this.fb.group({
        domain: ['', [Validators.required]],

        title: [''],
        firstname: ['', [Validators.required]],
        lastname: ['', [Validators.required]],
        email: ['', [Validators.required, RmaValidators.email]],
        phoneNumber: ['', [Validators.required]],

        companyName: ['', [Validators.required]],
        corporateForm: ['', [Validators.required]],
        website: [''],
        street: ['', [Validators.required]],
        buildingNumber: ['', [Validators.required]],
        additionalAddressline: [''],
        postoffice: [''],
        zip: ['', [Validators.required]],
        place: ['', [Validators.required]],
        country: ['', [Validators.required]],
        canton: [''],
      }),
      accountingInfo: this.fb.group({
        vatLiable: [false, []],
        vatBusinessNumber: ['', []],
        vatMethod: [VatMethod2.NONE, []],
        chartOfAccountType: ['', [Validators.required]],
        currency: ['', [Validators.required]],
        entryDate: ['', [Validators.required]],
        exitDate: ['', [Validators.required]],
        language: ['', [Validators.required]],
        onlineFiduciary: ['', [Validators.required]],
        onlineAccountant: ['', [Validators.required]],
        onlineSubstitute: ['', [Validators.required]],
        businessPurpose: [''],
      }),
      serviceInfo: this.fb.group({
        rmaServiceStartDate: ['', [Validators.required]],
        accountingPackage: ['', [Validators.required]],
        documentScanningModule: [false],
        expenseAppModule: [false],
        paymentWorkflowModule: [false],
        payrollPackage: [null],
        payrollPayDay: [null],
        executePayrollPayments: [false],
        executePayrollPaymentsBeforeBankHolidays: [false],
        refurbishDocuments: [false],
        enablePayrollPackage: [false],
      }),
      summaryInfo: this.fb.group({
        info: [''],
        files: [[]],
        isOrderConfirmed: [false, [Validators.required, this.orderConfirmedValidator()]]
      }),
    });
  }
}
